import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { ArrowLeft, FileSpreadsheet, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
const API = process.env.REACT_APP_BACKEND_URL + "/api/procurement";

export default function Comparison() {
  const navigate = useNavigate();
  const [mainCategories, setMainCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tags, setTags] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [itemsMap, setItemsMap] = useState({});
  const [summary, setSummary] = useState({});
  const [filterType, setFilterType] = useState('main_category');
  const [selectedMainCategory, setSelectedMainCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [selectedItem, setSelectedItem] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState('classic'); // 'classic' or 'matrix'

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [mainCatRes, subCatRes, itemsRes, projectsRes, tagsRes] = await Promise.all([
        axios.get(`${API}/main-categories`),
        axios.get(`${API}/sub-categories`),
        axios.get(`${API}/items`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/tags`)
      ]);
      setMainCategories(mainCatRes.data);
      setSubCategories(subCatRes.data);
      setItems(itemsRes.data);
      setProjects(projectsRes.data);
      setTags(tagsRes.data);
    } catch (error) {
      toast.error('خطأ في جلب البيانات');
    }
  };

  const handleCompare = async () => {
    let requestData = {};
    
    if (filterType === 'main_category' && !selectedMainCategory) {
      toast.error('الرجاء اختيار تصنيف رئيسي');
      return;
    }
    if (filterType === 'sub_category' && !selectedSubCategory) {
      toast.error('الرجاء اختيار تصنيف فرعي');
      return;
    }
    if (filterType === 'item' && !selectedItem) {
      toast.error('الرجاء اختيار صنف');
      return;
    }
    if (filterType === 'project' && !selectedProject) {
      toast.error('الرجاء اختيار مشروع');
      return;
    }
    if (filterType === 'tag' && !selectedTag) {
      toast.error('الرجاء اختيار تصنيف');
      return;
    }

    if (filterType === 'main_category') requestData.main_category_id = selectedMainCategory;
    else if (filterType === 'sub_category') requestData.sub_category_id = selectedSubCategory;
    else if (filterType === 'item') requestData.item_id = selectedItem;
    else if (filterType === 'project') requestData.project_id = selectedProject;
    else if (filterType === 'tag') requestData.tag_id = selectedTag;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/reports/compare`, requestData);
      setQuotes(response.data.quotes);
      setItemsMap(response.data.items_map);
      setSummary(response.data.summary);
      
      if (response.data.quotes.length === 0) {
        toast.info('لا توجد عروض أسعار');
      }
    } catch (error) {
      toast.error('خطأ في إنشاء التقرير');
    }
    setLoading(false);
  };

  const handleExport = async (format) => {
    let requestData = {};
    
    if (filterType === 'main_category') requestData.main_category_id = selectedMainCategory;
    else if (filterType === 'sub_category') requestData.sub_category_id = selectedSubCategory;
    else if (filterType === 'item') requestData.item_id = selectedItem;
    else if (filterType === 'project') requestData.project_id = selectedProject;
    else if (filterType === 'tag') requestData.tag_id = selectedTag;

    try {
      // Determine endpoint based on report type
      let endpoint;
      let fileName;
      let reportTypeName;
      
      if (reportType === 'matrix') {
        endpoint = `${API}/reports/export/${format}-matrix`;
        fileName = `price_comparison_matrix.${format === 'excel' ? 'xlsx' : 'pdf'}`;
        reportTypeName = 'مصفوفة';
      } else if (reportType === 'detailed') {
        endpoint = `${API}/reports/export/${format}-detailed`;
        fileName = `price_comparison_detailed.${format === 'excel' ? 'xlsx' : 'pdf'}`;
        reportTypeName = 'تفصيلي';
      } else {
        endpoint = `${API}/reports/export/${format}`;
        fileName = `price_comparison.${format === 'excel' ? 'xlsx' : 'pdf'}`;
        reportTypeName = 'عادي';
      }
      
      const response = await axios.post(
        endpoint,
        requestData,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`تم تصدير التقرير ${reportTypeName} بنجاح (${format.toUpperCase()})`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('خطأ في تصدير التقرير');
    }
  };

  const getChartData = () => {
    return Object.entries(itemsMap).flatMap(([itemName, itemQuotes]) => 
      itemQuotes.map(quote => ({
        name: `${quote.supplier_name}`,
        item: itemName,
        price: quote.price
      }))
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-slate-100 p-3 md:p-6">
      <div className="container mx-auto max-w-7xl">
        {/* Back button - Mobile priority */}
        <div className="mb-4">
          <Button 
            data-testid="back-to-dashboard-btn"
            onClick={() => navigate('/procurement')} 
            variant="outline"
            className="gap-2 min-w-[120px] w-full md:w-auto md:hidden"
          >
            <ArrowLeft className="w-5 h-5 shrink-0" />
            <span>العودة للرئيسية</span>
          </Button>
        </div>
        
        <div className="mb-6 md:mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-4xl font-bold text-slate-800 mb-2" style={{fontFamily: 'Manrope'}}>
              تقرير مقارنة الأسعار
            </h1>
            <p className="text-sm md:text-base text-slate-600">قارن عروض الأسعار وصدّر التقارير</p>
          </div>
          {/* Back button - Desktop only */}
          <Button 
            data-testid="back-to-dashboard-btn-desktop"
            onClick={() => navigate('/procurement')} 
            variant="outline"
            className="gap-2 min-w-[120px] shrink-0 hidden md:flex"
          >
            <ArrowLeft className="w-5 h-5 shrink-0" />
            <span>العودة</span>
          </Button>
        </div>

        <Card className="mb-8 border-0 shadow-xl backdrop-blur-sm bg-white/90">
          <CardHeader>
            <CardTitle className="text-2xl" style={{fontFamily: 'Manrope'}}>إعدادات المقارنة</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>نوع المقارنة</Label>
                  <Select value={filterType} onValueChange={(value) => {
                    setFilterType(value);
                    setQuotes([]);
                    setSummary({});
                    setItemsMap({});
                  }}>
                    <SelectTrigger data-testid="filter-type-select" className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent position="popper">
                      <SelectItem value="main_category">حسب التصنيف الرئيسي</SelectItem>
                      <SelectItem value="sub_category">حسب التصنيف الفرعي</SelectItem>
                      <SelectItem value="item">حسب الصنف</SelectItem>
                      <SelectItem value="project">حسب المشروع</SelectItem>
                      <SelectItem value="tag">حسب تصنيف العرض</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>نوع التقرير</Label>
                  <Select value={reportType} onValueChange={setReportType}>
                    <SelectTrigger data-testid="report-type-select" className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent position="popper">
                      <SelectItem value="classic">
                        <div className="flex flex-col">
                          <span className="font-semibold">تقرير عادي</span>
                          <span className="text-xs text-gray-500">جداول منفصلة + أصناف مشتركة وغير مشتركة</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="matrix">
                        <div className="flex flex-col">
                          <span className="font-semibold">تقرير مصفوفة ✨</span>
                          <span className="text-xs text-gray-500">جدول واحد + الموردين في أعمدة + الفرق</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="detailed">
                        <div className="flex flex-col">
                          <span className="font-semibold">تقرير تفصيلي 📊</span>
                          <span className="text-xs text-gray-500">ملخص + جدول منفصل لكل صنف + توصيات</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {filterType === 'main_category' && (
                <div>
                  <Label>اختر التصنيف الرئيسي</Label>
                  <Select value={selectedMainCategory} onValueChange={setSelectedMainCategory}>
                    <SelectTrigger data-testid="main-category-filter-select" className="mt-2">
                      <SelectValue placeholder="اختر التصنيف الرئيسي" />
                    </SelectTrigger>
                    <SelectContent position="popper">
                      {mainCategories.map(category => (
                        <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {filterType === 'sub_category' && (
                <div>
                  <Label>اختر التصنيف الفرعي</Label>
                  <Select value={selectedSubCategory} onValueChange={setSelectedSubCategory}>
                    <SelectTrigger data-testid="sub-category-filter-select" className="mt-2">
                      <SelectValue placeholder="اختر التصنيف الفرعي" />
                    </SelectTrigger>
                    <SelectContent position="popper">
                      {subCategories.map(category => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.name} - ({category.main_category_name})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {filterType === 'item' && (
                <div>
                  <Label>اختر الصنف</Label>
                  <Select value={selectedItem} onValueChange={setSelectedItem}>
                    <SelectTrigger data-testid="item-filter-select" className="mt-2">
                      <SelectValue placeholder="اختر الصنف" />
                    </SelectTrigger>
                    <SelectContent position="popper">
                      {items.map(item => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {filterType === 'project' && (
                <div>
                  <Label>اختر المشروع</Label>
                  <Select value={selectedProject} onValueChange={setSelectedProject}>
                    <SelectTrigger data-testid="project-filter-select" className="mt-2">
                      <SelectValue placeholder="اختر المشروع" />
                    </SelectTrigger>
                    <SelectContent position="popper">
                      {projects.map(project => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.project_number} - {project.project_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {filterType === 'tag' && (
                <div>
                  <Label>اختر التصنيف</Label>
                  <Select value={selectedTag} onValueChange={setSelectedTag}>
                    <SelectTrigger data-testid="tag-filter-select" className="mt-2">
                      <SelectValue placeholder="اختر التصنيف" />
                    </SelectTrigger>
                    <SelectContent position="popper">
                      {tags.map(tag => (
                        <SelectItem key={tag.id} value={tag.id}>
                          {tag.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <Button 
                data-testid="compare-btn"
                onClick={handleCompare} 
                disabled={loading}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:shadow-lg"
              >
                إنشاء المقارنة
              </Button>
            </div>
          </CardContent>
        </Card>

        {quotes.length > 0 && (
          <>
            <Card className="mb-8 border-0 shadow-xl backdrop-blur-sm bg-white/90">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-2xl" style={{fontFamily: 'Manrope'}}>تصدير التقرير</CardTitle>
                <div className="flex gap-2">
                  <Button 
                    data-testid="export-excel-btn"
                    onClick={() => handleExport('excel')} 
                    variant="outline"
                    className="gap-2 hover:bg-green-50 hover:text-green-700 hover:border-green-300"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    Excel
                  </Button>
                  <Button 
                    data-testid="export-pdf-btn"
                    onClick={() => handleExport('pdf')} 
                    variant="outline"
                    className="gap-2 hover:bg-red-50 hover:text-red-700 hover:border-red-300"
                  >
                    <FileText className="w-4 h-4" />
                    PDF
                  </Button>
                </div>
              </CardHeader>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {Object.entries(summary).map(([itemName, stats]) => (
                <Card key={itemName} className="border-0 shadow-lg backdrop-blur-sm bg-white/90">
                  <CardHeader>
                    <CardTitle className="text-lg" style={{fontFamily: 'Manrope'}}>{itemName}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                      <span className="text-sm text-slate-600">أقل سعر</span>
                      <span className="font-bold text-green-700">{stats.lowest_price} ريال</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-red-50 rounded">
                      <span className="text-sm text-slate-600">أعلى سعر</span>
                      <span className="font-bold text-red-700">{stats.highest_price} ريال</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-blue-50 rounded">
                      <span className="text-sm text-slate-600">متوسط السعر</span>
                      <span className="font-bold text-blue-700">{stats.average_price.toFixed(2)} ريال</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-slate-50 rounded">
                      <span className="text-sm text-slate-600">عدد العروض</span>
                      <span className="font-bold text-slate-700">{stats.quotes_count}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {Object.keys(itemsMap).length > 0 && (
              <Card className="mb-8 border-0 shadow-xl backdrop-blur-sm bg-white/90">
                <CardHeader>
                  <CardTitle className="text-2xl" style={{fontFamily: 'Manrope'}}>رسم بياني للمقارنة</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={getChartData()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="price" fill="#f97316" name="السعر (ريال)" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            <Card className="border-0 shadow-xl backdrop-blur-sm bg-white/90">
              <CardHeader>
                <CardTitle className="text-2xl" style={{fontFamily: 'Manrope'}}>جدول المقارنة التفصيلي</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-slate-200">
                        <th className="text-right p-3 font-semibold text-slate-700">المشروع</th>
                        <th className="text-right p-3 font-semibold text-slate-700">الصنف</th>
                        <th className="text-right p-3 font-semibold text-slate-700">المورد</th>
                        <th className="text-right p-3 font-semibold text-slate-700">السعر</th>
                        <th className="text-right p-3 font-semibold text-slate-700">الكمية</th>
                        <th className="text-right p-3 font-semibold text-slate-700">تاريخ العرض</th>
                        <th className="text-right p-3 font-semibold text-slate-700">ملاحظات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quotes.map((quote, idx) => {
                        const isLowest = quote.price === summary[quote.item_name]?.lowest_price;
                        return (
                          <tr 
                            key={quote.id} 
                            data-testid={`comparison-row-${idx}`}
                            className={`border-b border-slate-100 ${isLowest ? 'bg-green-50' : 'hover:bg-slate-50'}`}
                          >
                            <td className="p-3 text-slate-600">{quote.project_number || '-'}</td>
                            <td className="p-3 text-slate-800 font-medium">{quote.item_name}</td>
                            <td className="p-3 text-slate-600">{quote.supplier_name}</td>
                            <td className="p-3 text-slate-800 font-bold">
                              {quote.price} ريال
                              {isLowest && <span className="mr-2 text-xs text-green-700 font-normal">(أقل سعر)</span>}
                            </td>
                            <td className="p-3 text-slate-600">{quote.quantity}</td>
                            <td className="p-3 text-slate-600">{quote.quote_date}</td>
                            <td className="p-3 text-slate-600 text-sm">{quote.notes || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
